const content = `
# Glossary

Key terms used throughout Flux PhD and the forecasting pipeline.

## Energy & Power

| Term | Definition |
|------|-----------|
| **kW** | Kilowatt -- unit of instantaneous power (rate of energy use) |
| **kWh** | Kilowatt-hour -- unit of energy (power sustained for one hour) |
| **Peak Demand** | Maximum instantaneous power draw from the grid in a billing period |
| **Demand Charge** | Utility fee based on peak demand (dollars per kW per month) |
| **Load Profile** | Time-series of power consumption over 24 hours or 8,760 hours |
| **8760** | Shorthand for a full-year hourly dataset (365 days x 24 hours) |

## Equipment

| Term | Definition |
|------|-----------|
| **PV** | Photovoltaic solar panels that convert sunlight to DC electricity |
| **BESS** | Battery Energy Storage System -- stores and releases electrical energy |
| **SOC** | State of Charge -- battery energy level as a percentage of capacity (0-100%) |
| **DCFC** | DC Fast Charger -- high-power EV charger (50-350 kW) |
| **L2** | Level 2 EV charger -- standard AC charger (typically 7.7 kW) |
| **Inverter** | Converts DC to AC (or vice versa) with some efficiency loss |
| **Topology** | Power system architecture defining how components are wired together |

## Optimization

| Term | Definition |
|------|-----------|
| **PoP** | Peak optimization Percentage -- how close orchestrated peak is to the global optimum |
| **ADMM** | Alternating Direction Method of Multipliers -- distributed optimization algorithm |
| **LP** | Linear Program -- optimization with linear objective and constraints |
| **MILP** | Mixed-Integer Linear Program -- LP with some variables restricted to integers |
| **HiGHS** | Open-source LP/MILP solver used by Vortx |

## Forecasting

| Term | Definition |
|------|-----------|
| **ComStock** | NREL dataset of 350,000+ simulated commercial building energy profiles |
| **Cold-Start** | Forecasting with zero historical data -- only building metadata available |
| **Fine-Tuning** | Adjusting a cold-start forecast using a few days of real meter data |
| **LightGBM** | Gradient boosting framework used for component load models |
| **Optuna** | Bayesian hyperparameter optimization library |
| **MAPE** | Mean Absolute Percentage Error -- average relative forecast error |
| **R-squared** | Coefficient of determination -- fraction of variance explained by the model |
| **Component Model** | A model predicting one end-use category (HVAC, refrigeration, etc.) |
| **Climate Zone** | ASHRAE classification of geographic climate (1A through 7) |

## Architecture

| Term | Definition |
|------|-----------|
| **PhD API** | Flask backend service handling simulations, forecasting, and model management |
| **Platform API** | Central routing layer that authenticates requests and dispatches to backend services |
| **Solver** | Julia-based optimization engine (HiGHS LP/MILP) for peak shaving and arbitrage |
| **Firestore** | Google Cloud document database storing projects, scenarios, and simulation history |
| **Cloud Run** | Google Cloud serverless compute platform running all backend services |
`;

export default content;
